import Graphic from '@arcgis/core/Graphic';
import Sketch from '@arcgis/core/widgets/Sketch';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { Providable } from './providers';
import { GeometricShape, GeometricType } from '../types/geo';
import { GeographicRecord } from './data-fetcher';
import { DISABLED, ToolBarButtons, setGraphicToolState } from './toolbar-handler';
import { buildGraphic, updateGraphicColor } from './graphics-handler';
import LocalStoragePreferences from '../utils/local-storage-preferences';
import { GraphicStyle } from './graphic-styles';

export type SketchType = 'point' | 'polyline' | 'polygon' | 'rectangle' | 'circle';

const TYPE_MAPPING = { Point: 'point', LineString: 'polyline', Polygon: 'polygon' };

const selectedTool = (geometryTypes: GeometricType[]): SketchType => {
  let tool = new LocalStoragePreferences().lastSketchCreateTool || 'point';

  const supportedTypes = geometryTypes
    .filter((type) => TYPE_MAPPING[type])
    .map((type) => TYPE_MAPPING[type]);

  if (!supportedTypes.includes(tool)) {
    tool = supportedTypes[0] || 'point';
  }

  return tool as SketchType;
};

export const beginEditing = async (
  providable: Providable,
  sketch: Sketch,
  color: string,
  geometryTypes: GeometricType[],
  geometry?: GeometricShape,
): Promise<void> => {
  // eslint-disable-next-line no-param-reassign
  sketch.visible = true;

  if (!geometry) {
    const tool = selectedTool(geometryTypes);
    sketch.create(tool);
    return;
  }

  const graphic = buildGraphic(geometry, color, GraphicStyle.TARGET_GEOMETRY);
  const layer: GraphicsLayer = providable.viewLayer();
  layer.removeAll();
  providable.sketchLayer().graphics.add(graphic);
  sketch?.update(graphic, { tool: 'reshape' });
  await setGraphicToolState(ToolBarButtons, DISABLED);
};

export const correctMismatch = (
  providable: Providable,
  sketch: Sketch,
  geometryTypes: GeometricType[],
): void => {
  // eslint-disable-next-line no-param-reassign
  sketch.visible = true;

  const tool = selectedTool(geometryTypes);
  sketch.create(tool);

  const layer: GraphicsLayer = providable.viewLayer();
  layer.removeAll();
};

export const endEditing = (
  providable: Providable,
  sketch: Sketch,
  saveOperationInProgress: boolean,
  color: string,
  geometry?: GeometricShape,
): void => {
  // eslint-disable-next-line no-param-reassign
  sketch.visible = false;
  sketch.cancel();
  if (saveOperationInProgress) {
    return;
  }

  providable.sketchLayer().removeAll();
  if (geometry) {
    const graphic = buildGraphic(geometry, color, GraphicStyle.TARGET_GEOMETRY);
    providable.viewLayer().graphics.add(graphic);
  }
};

export const updateShapeColor = (
  providable: Providable,
  color: string,
) => {
  const layer = providable.sketchLayer();
  const sketch = providable.sketch();
  const { createGraphic } = sketch;

  layer.graphics.forEach((graphic: Graphic) => {
    updateGraphicColor(graphic, color);
  });

  if (createGraphic) {
    updateGraphicColor(createGraphic, color);
  }
};

export const replaceShape = (
  providable: Providable,
  geometry: GeometricShape|null|undefined,
  color: string,
) => {
  const viewLayer: GraphicsLayer = providable.viewLayer();
  const sketchLayer: GraphicsLayer = providable.sketchLayer();

  viewLayer.removeAll();
  sketchLayer.removeAll();

  if (geometry) {
    const sketch: Sketch = providable.sketch();
    const graphic = buildGraphic(geometry, color, GraphicStyle.TARGET_GEOMETRY);
    sketchLayer.graphics.add(graphic);
    sketch.cancel();
  }
};

export const reenableEditing = (
  providable: Providable,
  sketch: Sketch,
) => {
  const sketchLayer = providable.sketchLayer();
  const currentShape = sketchLayer.graphics.getItemAt(0);
  sketch.update(currentShape, { tool: 'reshape' });

  // eslint-disable-next-line no-param-reassign
  sketch.visible = true;
};

export const drawNearbyRecords = (
  providable: Providable, nearbyRecords: GeographicRecord[],
) => {
  const layer = providable.staticLayer();
  nearbyRecords.forEach((nearbyRecord: GeographicRecord) => {
    const { color, geometry, style } = nearbyRecord;
    if (geometry) {
      const graphic = buildGraphic(geometry, color, style ?? GraphicStyle.NEARBY_GEOMETRIES);
      layer.graphics.add(graphic);
    }
  });
};
